import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '../../../services/account'
import { DomainDto } from '../../../dtos/domain'
import { cancelNamedAxiosRequest } from '../../../config/axios-setup'
import { DashboardDomain } from '../types'

type DomainsState = {
    status: 'loading' | 'ready' | 'error'
    domains: DashboardDomain[]
    /** How many of the organization's domains were left out as non-production; zero until they are read. */
    excludedDomainCount: number
}

const DOMAINS_CANCELLATION_KEY = 'org-dashboard-domains'

/**
 * A test or dev copy of a production domain, and a preview domain, never reach a real audience, so the dashboard
 * neither lists them nor counts them. Demo domains are real domains of a demo organization and stay: an
 * organization made only of them must not render an empty dashboard. The rule reads the DTO's own getters so it
 * cannot drift from the platform's definition of a test or preview domain.
 */
const isProductionDomain = (domain: DomainDto): boolean => !domain.isTestDomain && !domain.isPreviewDomain

/**
 * The organization's production domains. The dashboard's range depends on their time zones, so everything else
 * waits for this read. The earlier domains stay available while a retry is in flight and when it fails, so a failed refresh
 * never turns a known organization into one with no domains. A failed read is an error; an empty list is an
 * organization with no domains.
 */
export const useOrgDashboardDomains = (organizationId: number) => {
    const [state, setState] = useState<DomainsState>({
        status: 'loading',
        domains: [],
        excludedDomainCount: 0,
    })
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
        let cancelled = false
        setState((previous) => ({ ...previous, status: 'loading' }))
        Container.get(AccountService)
            .fetchAccountDomainsOrFailure(organizationId, DOMAINS_CANCELLATION_KEY)
            .then((domainDtos) => {
                if (cancelled) return
                if (!domainDtos) {
                    setState((previous) => ({ ...previous, status: 'error' }))
                    return
                }
                const productionDomainDtos = domainDtos.filter(isProductionDomain)
                setState({
                    status: 'ready',
                    excludedDomainCount: domainDtos.length - productionDomainDtos.length,
                    domains: productionDomainDtos.map((domain) => ({
                        id: domain.id,
                        displayName: domain.displayName,
                        hostname: domain.name,
                        currencyCode: domain.currencyCode || undefined,
                        timezone: domain.timezone,
                        ecommerceEnabled: domain.ecommConfig != null,
                    })),
                })
            })
            .catch(() => {
                if (!cancelled) setState((previous) => ({ ...previous, status: 'error' }))
            })
        return () => {
            cancelled = true
            cancelNamedAxiosRequest(DOMAINS_CANCELLATION_KEY)
        }
    }, [organizationId, attempt])

    return { ...state, retry: useCallback(() => setAttempt((count) => count + 1), []) }
}
